import { Injectable } from '@angular/core';
import { EChartsOption } from 'echarts';
import { Observable, Subject } from 'rxjs';
import { ClimateChart, ChartConfig, YAxis } from '../../../model/climate/climate-chart';
import { MAPTYCS_CHART_COLOR, getSSPColor } from './custom-theme';
import { ClimateChartHelper } from './util/climate-chart-helper';

@Injectable()
export class SeaLevelRiseWarmingService {

  private readonly locationChartSubject: Subject<any>;
  private readonly addressChartSubject: Subject<any>;

  constructor() {
    this.locationChartSubject = new Subject<any>();
    this.addressChartSubject = new Subject<any>();
  }

  emitLocationChartEvent(chartInstance: any): void {
    this.locationChartSubject.next(chartInstance);
  }

  getLocationChartSubject(): Observable<any> {
    return this.locationChartSubject.asObservable();
  }

  emitAddressChartEvent(chartInstance: any): void {
    this.addressChartSubject.next(chartInstance);
  }

  getAddressChartSubject(): Observable<any> {
    return this.addressChartSubject.asObservable();
  }

  options: EChartsOption = {
    height: '80%',
    animation: false,
    animationDuration: 0,
    animationDurationUpdate: 0,
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: '#6a7985'
        }
      },
      valueFormatter: (value: any) => typeof value === 'number' ? value.toFixed(3) : value
    },
    legend: {
      top: 10,
      left: 'center'
    },
    toolbox: {
      show: true,
      orient: 'vertical',
      feature: {
        show: true,
        dataView: { readOnly: true, title: 'Data View', lang: ['', 'Close'], buttonColor: MAPTYCS_CHART_COLOR },
        magicType: { type: ['line', 'bar'], title: { line: 'Switch to Line Chart', bar: 'Switch to Bar Chart' } },
        saveAsImage: { 
          title: 'Save as an image',
          name: 'Sea_Level_Rise_Warming',
          pixelRatio: 2,
          onclick: function() {
            const chartInstance = (this as any).chart;
            if (chartInstance) {
              chartInstance.setOption({
                title: {
                  text: 'Sea Level Rise Warming',
                  left: 'center',
                  bottom: 5,
                  textStyle: { fontSize: 14, fontWeight: 'bold' }
                }
              });
              setTimeout(() => {
                const url = chartInstance.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' });
                chartInstance.setOption({ title: { text: '' } });
                const link = document.createElement('a');
                link.download = 'Sea_Level_Rise_Warming.png';
                link.href = url;
                link.click();
              }, 100);
            }
          }
        }
      },
    },
    dataZoom: {
      show: false,
      start: 0,
      end: 100
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '12%',
      top: '20%',
      containLabel: true
    },
    xAxis: [
      { 
        type: 'category',
        boundaryGap: ['10%', '10%'],
        axisLabel: { interval: 0 },
      }
    ],
    yAxis: [
      {
        type: 'value',
        scale: true,
        name: 'Meter',
        position: 'left',
        alignTicks: true,
        axisLine: {
          show: true,
        },
        axisLabel: {
          formatter: '{value} m'
        },
        boundaryGap: [0.2, 0.2]
      }
    ],
    series: [],
    dataset: {}
  };

  updateSeaLevelRiseWarmingChart(echartsInstance: any, climateChart: ClimateChart) {
    const chartConfig: ChartConfig = climateChart.chartConfig;
    const yAxis: YAxis = chartConfig.yAxis;
    const seriesData = [];
    const source = [];
    source.push(climateChart.xAxis);

    for (const [key, value] of Object.entries(climateChart.series)) {
      seriesData.push(ClimateChartHelper.getSeriesConfig(key, chartConfig.type, getSSPColor));
      // Use common method to pad SSP scenario data
      source.push(ClimateChartHelper.padSSPScenarioData(key, value, climateChart.xAxis));
    }

    const bounds = ClimateChartHelper.calculateAxisBounds(
      climateChart.series, yAxis.min, yAxis.max, yAxis.interval
    );

    echartsInstance.setOption({
      animation: false,
      animationDuration: 0,
      animationDurationUpdate: 0,
      xAxis: [
        { 
          type: 'category',
          boundaryGap: ['10%', '10%'],
          axisLabel: { interval: 0 },
        }
      ],
      yAxis: [
        {
          type: 'value',
          scale: true,
          name: yAxis.title,
          min: bounds.min,
          max: bounds.max,
          interval: bounds.interval,
          position: 'left',
          alignTicks: true,
          axisLine: {
            show: true,
          },
          axisLabel: {
            formatter: '{value} ' + this.setUnit(yAxis.unit)
          },
          boundaryGap: [0.2, 0.2]
        }
      ],
      dataset: {
        source: source
      },
      series: seriesData
    });
    if (echartsInstance && typeof echartsInstance.on === 'function') {
      let applying = false;
      try { echartsInstance.off && echartsInstance.off('finished'); } catch {}
      echartsInstance.on('finished', () => {
        if (applying) return;
        applying = true;
        echartsInstance.setOption({
          xAxis: [{
            type: 'category',
            boundaryGap: ['10%', '10%'],
            axisLabel: { interval: 0 }
          }]
        }, { replaceMerge: ['xAxis'] });
        setTimeout(() => { applying = false; }, 0);
      });
    }
  }

  setUnit(unit: string) {
    if (unit === 'm') {
      return 'mm';
    }
    return unit;
  }
}
